// src/components/Subscription/SmartBanner.js
// P5.3 — Trial/Subscription Smart Banner
// Contextual alert banner for subscription states:
//   • Trial expiring (< 7 days / < 24 hours)
//   • Subscription expired/cancelled
//   • Payment failed
// Dismissable per session, gradient backgrounds, responsive layout.

import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Button,
  IconButton,
  Typography,
  Collapse,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../../contexts/SubscriptionContext';

// ─── Gradient Palettes ──────────────────────────────────────────────
const GRADIENTS = {
  warning: 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)',
  error:   'linear-gradient(135deg, #ff5f6d 0%, #ffc371 100%)',
  info:    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
};

// Icon per severity
const SEVERITY_ICON = {
  warning: WarningAmberIcon,
  error:   ErrorOutlineIcon,
  info:    InfoOutlinedIcon,
};

// ─── Banner-type resolver ───────────────────────────────────────────
/**
 * Determines which banner to show (if any) based on subscription state.
 *
 * @param {Object|null} subscription — from SubscriptionContext
 * @returns {{ type: string, severity: string, title: string, message: string, cta: string, href: string } | null}
 */
function resolveBanner(subscription) {
  if (!subscription) return null;

  const { status, plan, trialEnd } = subscription;

  // 1. Payment failed — top priority
  if (status === 'past_due' || status === 'unpaid' || status === 'incomplete') {
    return {
      type: 'payment_failed',
      severity: 'error',
      title: 'Payment failed',
      message: "We couldn't process your payment. Update your billing info to keep your access.",
      cta: 'Update Billing',
      href: '/subscription/manage',
    };
  }

  // 2. Trial states (only when status is 'trialing' and trialEnd exists)
  if (status === 'trialing' && trialEnd) {
    const now = Date.now();
    const end = new Date(trialEnd).getTime();
    const msRemaining = end - now;

    if (msRemaining <= 0) {
      // Trial already expired but status hasn't flipped yet
      return {
        type: 'trial_expired',
        severity: 'error',
        title: 'Trial expired',
        message: 'Your free trial has ended. Upgrade now to keep your premium features.',
        cta: 'Upgrade Now',
        href: '/subscription/plans',
      };
    }

    const daysRemaining = Math.ceil(msRemaining / (1000 * 60 * 60 * 24));
    const hoursRemaining = msRemaining / (1000 * 60 * 60);

    // < 24 hours — urgent
    if (hoursRemaining < 24) {
      return {
        type: 'trial_last_day',
        severity: 'error',
        title: 'Last day of trial!',
        message: "Your trial expires today. Don't lose access to AI insights and more.",
        cta: 'Upgrade Now',
        href: '/subscription/plans',
      };
    }

    // < 7 days — warning
    if (daysRemaining <= 7) {
      return {
        type: 'trial_expiring',
        severity: 'warning',
        title: 'Trial ending soon',
        message: `Your free trial ends in ${daysRemaining} day${daysRemaining === 1 ? '' : 's'}. Upgrade to keep your premium features.`,
        cta: 'Upgrade Now',
        href: '/subscription/plans',
      };
    }

    // Trial with plenty of time — no banner
    return null;
  }

  // 3. Subscription cancelled / expired / free plan (only show if not actively subscribed)
  if (
    status === 'cancelled' ||
    status === 'incomplete_expired' ||
    (plan === 'free' && status === 'active')
  ) {
    // Don't nag free users who've never had a paid plan
    // We only show this if the status explicitly indicates cancellation / expiry
    if (status === 'cancelled' || status === 'incomplete_expired') {
      return {
        type: 'subscription_inactive',
        severity: 'info',
        title: 'Subscription inactive',
        message: "You're on the free plan. Upgrade to unlock all features.",
        cta: 'See Plans',
        href: '/subscription/plans',
      };
    }
    return null;
  }

  return null;
}

// ─── Session-storage helpers ────────────────────────────────────────
function isDismissed(bannerType) {
  try {
    return sessionStorage.getItem(`banner_dismissed_${bannerType}`) === 'true';
  } catch {
    return false;
  }
}

function dismiss(bannerType) {
  try {
    sessionStorage.setItem(`banner_dismissed_${bannerType}`, 'true');
  } catch {
    // Private / incognito mode — silently ignore
  }
}

// ─── SmartBanner Component ──────────────────────────────────────────
const SmartBanner = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { subscription, loading } = useSubscription();

  // Resolve which banner (if any) to show
  const banner = useMemo(() => resolveBanner(subscription), [subscription]);

  // Visibility state (controls Collapse animation)
  const [visible, setVisible] = useState(false);

  // Determine initial visibility on mount / when banner type changes
  useEffect(() => {
    if (banner && !isDismissed(banner.type)) {
      // Small delay so the Collapse animates in after mount
      const timeout = setTimeout(() => setVisible(true), 120);
      return () => clearTimeout(timeout);
    }
    setVisible(false);
  }, [banner]);

  // Nothing to show
  if (loading || !banner || isDismissed(banner?.type)) return null;

  const SeverityIcon = SEVERITY_ICON[banner.severity] || InfoOutlinedIcon;
  const gradient = GRADIENTS[banner.severity] || GRADIENTS.info;

  const handleDismiss = () => {
    setVisible(false);
    // Wait for collapse animation to finish before marking dismissed
    setTimeout(() => dismiss(banner.type), 350);
  };

  const handleCTA = () => {
    navigate(banner.href);
  };

  return (
    <Collapse in={visible} timeout={350}>
      <Box
        role="alert"
        aria-live="polite"
        sx={{
          width: '100%',
          background: gradient,
          position: 'relative',
          zIndex: theme.zIndex.appBar - 1,
          // Subtle inner shadow for depth
          boxShadow: 'inset 0 -1px 0 rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.15)',
          py: isMobile ? 1.25 : 0.75,
          px: isMobile ? 2 : 3,
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-start' : 'center',
          gap: isMobile ? 1 : 2,
          minHeight: isMobile ? 'auto' : 48,
        }}
      >
        {/* Icon + Text */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flex: 1,
            minWidth: 0,
            pr: isMobile ? 4 : 0, // Leave room for close button on mobile
          }}
        >
          <SeverityIcon
            sx={{
              color: '#fff',
              fontSize: 22,
              flexShrink: 0,
              filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))',
            }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              sx={{
                color: '#fff',
                fontWeight: 700,
                fontSize: '0.8rem',
                lineHeight: 1.3,
                textShadow: '0 1px 2px rgba(0,0,0,0.15)',
                letterSpacing: '0.02em',
              }}
            >
              {banner.title}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255,255,255,0.92)',
                fontSize: '0.78rem',
                lineHeight: 1.35,
                textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                whiteSpace: isMobile ? 'normal' : 'nowrap',
                overflow: isMobile ? 'visible' : 'hidden',
                textOverflow: isMobile ? 'unset' : 'ellipsis',
              }}
            >
              {banner.message}
            </Typography>
          </Box>
        </Box>

        {/* CTA Button */}
        <Button
          size="small"
          variant="contained"
          startIcon={<RocketLaunchIcon sx={{ fontSize: '16px !important' }} />}
          onClick={handleCTA}
          sx={{
            flexShrink: 0,
            backgroundColor: 'rgba(255,255,255,0.95)',
            color: banner.severity === 'warning' ? '#b45309' :
                   banner.severity === 'error'   ? '#b91c1c' : '#4338ca',
            fontWeight: 700,
            fontSize: '0.75rem',
            textTransform: 'none',
            borderRadius: '20px',
            px: 2.5,
            py: 0.5,
            minHeight: 32,
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
            '&:hover': {
              backgroundColor: '#fff',
              boxShadow: '0 4px 14px rgba(0,0,0,0.22)',
              transform: 'translateY(-1px)',
            },
            transition: 'all 0.2s ease',
            alignSelf: isMobile ? 'stretch' : 'center',
          }}
        >
          {banner.cta}
        </Button>

        {/* Close Button — absolute positioned on mobile, inline on desktop */}
        <IconButton
          aria-label="Dismiss banner"
          size="small"
          onClick={handleDismiss}
          sx={{
            color: 'rgba(255,255,255,0.85)',
            position: isMobile ? 'absolute' : 'relative',
            top: isMobile ? 8 : 'auto',
            right: isMobile ? 8 : 'auto',
            flexShrink: 0,
            '&:hover': {
              color: '#fff',
              backgroundColor: 'rgba(255,255,255,0.15)',
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Collapse>
  );
};

export default SmartBanner;
